<template>
  <div>
    <transition name="fade">

      <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 p-0">
        <div class="bg-white w-full h-full overflow-auto relative flex flex-col">

          <!-- Header (uniform with ArticleModal) -->
          <div
            class="flex justify-between items-center p-4 border-b shadow-md bg-[#D7642A] text-white sticky top-0 z-20">
            <h2 class="text-2xl font-bold">{{ postToEdit ? 'Edit Post' : 'Create Post' }}</h2>
            <button @click="closeModal" class="text-white hover:text-gray-100 text-2xl font-bold">✕</button>
          </div>

          <!-- Left + Right Columns -->
          <div class="flex flex-col md:flex-row flex-1 p-4 md:p-6 space-y-4 md:space-y-0">

            <!-- Left: File Upload + Preview -->
            <div
              class="w-full md:w-1/2 flex flex-col gap-4 md:gap-6 border-b md:border-b-0 md:border-r border-gray-200 flex-shrink-0 px-2 md:px-4">

              <!-- Upload Section -->
              <div class="flex flex-col flex-1 min-h-[150px]">
                <p class="mb-2 font-semibold text-gray-700">Upload Image</p>
                <div
                  class="relative w-full h-full border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors group hover:border-primary-dark hover:bg-primary-light-header"
                  :class="isDragging ? 'border-primary-dark bg-primary-light-header' : 'border-gray-300 bg-white'"
                  @click="fileInput.click()" @dragover.prevent="isDragging = true"
                  @dragleave.prevent="isDragging = false" @drop.prevent="handleFileUpload($event)">

                  <!-- Upload Icon -->
                  <svg v-if="!uploadedFile" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                    class="h-12 w-12 text-gray-300 mb-2" fill="currentColor" opacity="0.7">
                    <path
                      d="M13.0001 7.82843V20H11.0001V7.82843L5.63614 13.1924L4.22192 11.7782L12.0001 4L19.7783 11.7782L18.3641 13.1924L13.0001 7.82843Z" />
                  </svg>

                  <!-- Instructions -->
                  <span v-if="!uploadedFile" class="text-gray-500 text-sm text-center px-4">
                    Click or drag & drop to upload<br />
                    <span class="text-xs text-gray-400">(Max size: 5MB, JPG/PNG only)</span>
                  </span>

                  <!-- Uploaded file name -->
                  <div v-else class="flex flex-col items-center text-gray-700">
                    <span class="font-medium">{{ uploadedFile.name }}</span>
                  </div>
                </div>
                <input type="file" class="hidden" ref="fileInput" @change="handleFileUpload" />
              </div>

              <!-- Preview Section -->
              <div class="flex flex-col flex-1 min-h-[150px] mt-2 md:mt-4">
                <p class="mb-2 font-semibold text-gray-700">Image Preview</p>
                <div
                  class="relative w-full h-full border rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">

                  <img v-if="filePreview || postToEdit?.media_link"
                    :src="filePreview ? filePreview : `${imageDirectory}${postToEdit.media_link}`"
                    class="w-full h-full object-cover" />

                  <!-- Overlay Buttons -->
                  <div v-if="filePreview || postToEdit?.media_link"
                    class="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center gap-2 opacity-0 hover:opacity-100 transition">
                    <button @click.stop="removeFile"
                      class="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-sm">Delete</button>
                    <button @click.stop="fileInput.click()"
                      class="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-sm">Replace</button>
                  </div>

                  <span v-if="!filePreview && !postToEdit?.media_link" class="text-gray-400 text-sm">
                    No preview available
                  </span>
                </div>
              </div>
            </div>

            <!-- Right: Form Inputs -->
            <div class="w-full md:w-1/2 flex flex-col flex-1 space-y-4 overflow-auto relative px-2 md:px-4">

              <!-- Header -->
              <h2 class="text-lg font-medium mb-3 text-gray-800">
                {{ postToEdit ? "Edit Post" : "Create Post" }}
              </h2>

              <form @submit.prevent="openConfirm" class="space-y-3">

                <!-- Title -->
                <div>
                  <label class="block text-gray-700 text-sm mb-1">Title:</label>
                  <input v-model="title" type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                    placeholder="Enter post title" required />
                </div>

                <!-- Content -->
                <PrimeEditor v-model="content" editorStyle="height: 240px">
                  <template v-slot:toolbar>
                    <EditorToolbar />
                  </template>
                </PrimeEditor>

                <!-- Status -->
                <div>

                  <label class="block font-semibold mb-1 text-gray-700">Status:</label>

                  <div class="flex gap-2 flex-wrap">

                    <button type="button" @click="status = 'Draft'"
                      class="px-4 py-2 rounded border text-sm font-medium transition"
                      :class="status === 'Draft' ? 'bg-gray-300 text-gray-800 border-gray-300' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'">
                      Draft
                    </button>

                    <button type="button" @click="status = 'Published'"
                      class="px-4 py-2 rounded border text-sm font-medium transition"
                      :class="status === 'Published' ? 'bg-green-200 text-green-800 border-green-200' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'">
                      Published
                    </button>

                    <button type="button" @click="status = 'Archived'"
                      class="px-4 py-2 rounded border text-sm font-medium transition"
                      :class="status === 'Archived' ? 'bg-yellow-200 text-yellow-900 border-yellow-200' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'">
                      Archived
                    </button>

                  </div>

                </div>

                <!-- Tags & Audience -->
                <div v-if="showMeta" class="space-y-4">

                  <!-- Tags -->
                  <div>
                    <label class="block font-semibold mb-1 text-gray-700">Tags:</label>
                    <div class="flex gap-2 flex-wrap mb-2">
                      <button v-for="tag in tags" :key="tag.id" type="button" @click="toggleTag(tag.id)"
                        class="px-4 py-2 rounded border text-sm font-medium transition"
                        :class="selectedTags.includes(tag.id) ? 'bg-orange-200 text-orange-800 border-orange-200' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'">
                        {{ tag.name }}
                      </button>
                      <div v-if="tags.length === 0" class="text-gray-500 text-xs mt-1">None</div>
                    </div>
                  </div>

                  <!-- Audience -->
                  <div>
                    <label class="block font-semibold mb-1 text-gray-700">Audience:</label>
                    <div v-if="!isSystemAdvisory" class="flex gap-3 flex-wrap mb-3">
                      <button v-for="aud in audiences" :key="aud.id" type="button" @click="toggleAudience(aud.id)"
                        class="px-4 py-2 rounded border text-sm font-medium transition"
                        :class="selectedAudiences.includes(aud.id) ? 'bg-orange-200 text-orange-800 border-orange-200' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'">
                        {{ aud.name }}
                      </button>
                      <div v-if="audiences.length === 0" class="text-gray-500 text-xs mt-1">None</div>
                    </div>
                    <div v-else class="text-gray-800 text-sm">Audience: General (default)</div>
                  </div>

                </div>

                <!-- Submit -->
                <button type="submit"
                  class="w-full bg-orange-500 text-white font-medium py-3 mt-5 rounded hover:bg-orange-600 text-sm">
                  {{ postToEdit ? "Update Post" : "Submit" }}
                </button>
                <p v-if="errorMessage" class="text-red-500 text-sm mt-2">{{ errorMessage }}</p>

              </form>
            </div>
          </div>
        </div>
      </div>

    </transition>

    <ConfirmationModal :show="showConfirm" title="Confirm Submission"
      :message="`Are you sure you want to ${postToEdit ? 'update' : 'create'} this post?`" :loading="loading"
      confirm-text="Yes" cancel-text="Cancel" @confirm="confirmSubmit" @cancel="closeConfirm" />

  </div>

</template>

<script>

import { useRoute } from "vue-router";
import { ref, computed, watch, onMounted, inject } from "vue";

import { getModuleTagsBySection } from "@/api/moduleTags";
import { getAllModuleAudiences } from "@/api/moduleAudience";
import { createModulePost, updateModulePost } from "@/api/modulePosts";

import EditorToolbar from "@/components/ui/EditorToolbar.vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";


export default {
  props: { show: Boolean, postToEdit: { type: Object, default: null }, showMeta: { type: Boolean, default: true } },
  emits: ["close"],
  components: { ConfirmationModal, EditorToolbar },
  setup(props, { emit }) {
    const route = useRoute();
    const tags = ref([]), audiences = ref([]);
    const title = ref(""), content = ref(""), status = ref("Draft"), selectedTags = ref([]), selectedAudiences = ref([]);
    const uploadedFile = ref(null), fileInput = ref(null), isDragging = ref(false);
    const loading = ref(false), errorMessage = ref(""), showConfirm = ref(false), postToSubmit = ref(null);
    const refreshSection = inject("refreshSection");
    const isSystemAdvisory = computed(() => route.path.includes("system-advisories"));
    const generalAudienceId = 1;

    const existingImage = ref(null);
    const imageDirectory = process.env.VUE_APP_GAZETTE_IMG_DIRECTORY

    const fetchOptions = async () => {
      try {
        const audRes = await getAllModuleAudiences();
        audiences.value = audRes.data.data || [];
        const sectionId = route.query.sectionId;
        if (sectionId) {
          const tagRes = await getModuleTagsBySection(sectionId);
          tags.value = tagRes.data.data || [];
        }
      } catch (e) { console.error(e); }
    };

    const prefillForm = () => {
      if (props.postToEdit) {
        title.value = props.postToEdit.title || "";
        content.value = props.postToEdit.content || "";
        status.value = props.postToEdit.status || "Draft";
        selectedTags.value = props.postToEdit.tags?.map(t => t.id) || [];
        selectedAudiences.value = props.postToEdit.audiences?.map(a => a.id) || [];

        uploadedFile.value = null; // don't fake a file
        existingImage.value = props.postToEdit.media_link
          ? `${imageDirectory}${props.postToEdit.media_link}`
          : null;
      } else {
        title.value = content.value = "";
        status.value = "Draft";
        selectedTags.value = [];
        selectedAudiences.value = [];
        uploadedFile.value = null;
        existingImage.value = null;
      }
    };

    watch(() => props.postToEdit, prefillForm);
    onMounted(fetchOptions);

    const handleFileUpload = (event) => {
      const file = event.dataTransfer?.files?.[0] || event.target.files?.[0] || null;
      uploadedFile.value = file;
      isDragging.value = false;
    };

    const removeFile = () => { uploadedFile.value = null; };
    const toggleTag = id => selectedTags.value.includes(id) ? selectedTags.value = selectedTags.value.filter(t => t !== id) : selectedTags.value.push(id);
    const toggleAudience = id => selectedAudiences.value.includes(id) ? selectedAudiences.value = selectedAudiences.value.filter(a => a !== id) : selectedAudiences.value.push(id);

    const openConfirm = () => {
      if (!title.value.trim()) { errorMessage.value = "Title is required"; return; }
      postToSubmit.value = {
        section_id: props.postToEdit?.section_id || route.query.sectionId,
        title: title.value, content: content.value, status, tagIds: [...selectedTags.value], audienceIds: isSystemAdvisory.value ? [generalAudienceId] : [...selectedAudiences.value], file: uploadedFile.value
      };
      showConfirm.value = true;
    };


    const confirmSubmit = async () => {
      if (!postToSubmit.value) return;
      loading.value = true;
      errorMessage.value = "";
      try {
        let payload;
        if (postToSubmit.value.file) {
          payload = new FormData();
          for (const key of ["section_id", "title", "content", "status", "tagIds", "audienceIds", "isPinned"]) {
            const val = postToSubmit.value[key];
            if (val !== undefined) { Array.isArray(val) ? val.forEach(v => payload.append(`${key}[]`, v)) : payload.append(key, val); }
          }
          payload.append("file", postToSubmit.value.file);
        } else payload = postToSubmit.value;
        if (props.postToEdit) await updateModulePost(props.postToEdit.id, payload);
        else await createModulePost(payload);
        if (refreshSection) await refreshSection();
        closeModal();
      } catch (err) { errorMessage.value = err.response?.data?.message || "Failed to submit post"; console.error(err); }
      finally { loading.value = false; closeConfirm(); }
    };

    const closeConfirm = () => { showConfirm.value = false; postToSubmit.value = null; };

    const closeModal = () => emit("close");

    const filePreview = computed(() => {

      if (uploadedFile.value) {
        if (uploadedFile.value.type.startsWith("image/")) return URL.createObjectURL(uploadedFile.value);
        return null;
      }

      return existingImage.value;

    });

    return {
      tags, audiences, title, content, status, selectedTags, selectedAudiences, uploadedFile, imageDirectory,
      fileInput, isDragging, loading, errorMessage, showConfirm, postToSubmit, isSystemAdvisory,
      handleFileUpload, removeFile, toggleTag, toggleAudience, openConfirm, confirmSubmit, closeConfirm, closeModal, filePreview
    };

  }
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>